<script setup lang="ts">

import {
    Dialog,
    DialogDescription,
    DialogHeader,
    DialogScrollContent,
    DialogTitle,
    DialogTrigger
} from "@/core/components/ui/dialog";
import {Attachment} from "@/attachment/interfaces/attachment";
import BaseButton from "@/core/components/base/BaseButton.vue";
import Icon from "@/core/components/icon/Icon.vue";
import BaseImage from "@/core/components/base/BaseImage.vue";
import {computed, nextTick, ref, watch} from "vue";
import {attachmentApi, trackerApi} from "@/container";
import {TrackerType} from "@/core/http/resources/TrackerResource";
import storageService from "@/core/services/StorageService";
import BaseFlexSpinner from "@/core/components/base/spinner/BaseFlexSpinner.vue";
import AttachmentPrefillerOptionsModal from "@/attachment/components/AttachmentPrefillerOptionsModal.vue";
import {AttachmentPrefillerOption} from "@/core/http/resources/attachment/AttachmentResource";
import {usePatientStore} from "@/stores/patient.store";

const {attachment, consumerKey} = defineProps<{
    attachment: Attachment
    isPrefillable: boolean;
    consumerKey?: string;
}>();

const loading = ref(false);
const url = ref<null | string>(null);
const open = defineModel<boolean>("open");
const isDownloading = ref(false);
const openPrefillerModal = ref(false);
const prefillerOptions = ref<AttachmentPrefillerOption[]>();
const {getConsumer} = usePatientStore();

const consumer = computed(() => {
    if (consumerKey) {
        return getConsumer(consumerKey);
    }

    return undefined;
});

const handleVideo = async (attachment: Attachment) => {
    await nextTick();

    const video = document.getElementById(`video_${attachment.id}`) as HTMLVideoElement;
    const token = await storageService().read('token');

    video.src = import.meta.env.VITE_API_URL + '/attachments/' + attachment.id + '/render?token=' + token;
    await video.play();
};

const handleAttachment = (attachment: Attachment) => {
    trackerApi().track(TrackerType.ATTACHMENT_READ, attachment.id, 'attachment');

    if (!attachment.type && attachment.external_link) {
        return;
    }

    switch (attachment.type) {
        case 'document':
        case 'image':
            loading.value = true;
            attachmentApi().render(attachment.id)
                .then((response) => {
                    const blob = new Blob([response], {type: response.type});
                    url.value = window.URL.createObjectURL(blob);
                })
                .finally(() => {
                    loading.value = false;
                });
            break;
        case 'video':
            handleVideo(attachment);
            break;
    }
};

watch(() => attachment, (value) => {
    handleAttachment(value);
}, {immediate: true, deep: true});

/**
 * Download the video.
 */
const downloadVideo = () => {
    isDownloading.value = true;
    attachmentApi().render(attachment.id)
        .then((response) => {
            const blob = new Blob([response], {type: response.type});
            const url = window.URL.createObjectURL(blob);

            const a = document.createElement('a');
            a.href = url;

            a.download = attachment.title + '.mp4';

            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        })
        .finally(() => {
            isDownloading.value = false;
        });
};

/**
 * Action when the print the compliance document button is pressed.
 */
const printCompliance = async () => {
    if (attachment?.id) {
        openPrefillerModal.value = true;
    }
};

/**
 * Download a file.
 */
const downloadFile = () => {
    if (url.value && attachment.type === 'document') {
        const a = document.createElement('a');
        a.href = url.value;

        a.download = attachment.title + '.pdf';

        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        trackerApi().track(TrackerType.ATTACHMENT_DOWNLOAD, attachment.id, 'attachment');
    }
};
</script>

<template>
  <Dialog v-model:open="open">
    <DialogTrigger as-child>
      <slot />
    </DialogTrigger>
    <DialogScrollContent class="flex min-w-[90vw] md:min-w-[80vw] lg:min-w-[70vw] xl:min-w-[80vw] flex-col">
      <DialogHeader>
        <div class="flex items-center">
          <div v-if="attachment.owner?.logo" class="flex justify-center">
            <img :src="attachment.owner.logo.url" alt="owner logo" class="h-12">
          </div>
          <div class="flex flex-col justify-start text-start">
            <DialogTitle>
              {{ attachment.title }}
            </DialogTitle>
            <DialogDescription v-if="attachment.description" class="self-start">
              {{ attachment.description }}
            </DialogDescription>
          </div>
        </div>
      </DialogHeader>
      <div v-if="loading">
        <BaseFlexSpinner />
      </div>
      <div v-else>
        <div v-if="attachment.type === 'video'" class="space-y-2 flex flex-col">
          <div class="relative w-full aspect-video max-h-[70vh] bg-black rounded-lg overflow-hidden">
            <video
              :id="`video_${attachment.id}`"
              class="absolute inset-0 w-full h-full object-contain"
              preload="auto"
              controls
            />
          </div>
          <BaseButton
            primary
            class="self-center"
            prepend-icon="mdi-download"
            :loading="isDownloading"
            @click="downloadVideo()"
          >
            Télécharger
          </BaseButton>
        </div>
        <div v-else-if="attachment.type === 'image' && url">
          <base-image
            :id="`program-attachment-video_${attachment.id}`"
            :src="url"
            class="w-[100%] h-[500px]"
          />
        </div>
        <div v-else>
          <div v-if="url">
            <div class="flex flex-row flex-1 justify-end gap-4">
              <div v-if="isPrefillable">
                <div class="flex pb-2">
                  <BaseButton
                    primary
                    class="self-center"
                    prepend-icon="mdi-file-document-edit-outline"
                    @click="printCompliance()"
                  >
                    Préremplir
                  </BaseButton>
                  <AttachmentPrefillerOptionsModal
                    v-model:open="openPrefillerModal"
                    :attachment="attachment"
                    :options="prefillerOptions"
                    :consumer="consumer"
                    @on-close="openPrefillerModal=false"
                  />
                </div>
              </div>
              <div class="flex pb-2">
                <BaseButton
                  primary
                  class="self-center"
                  prepend-icon="mdi-download"
                  @click="downloadFile()"
                >
                  Télécharger
                </BaseButton>
              </div>
            </div>
            <iframe v-if="url" :src="url" class="w-[100%] h-[70vh]" />
          </div>
          <div v-else-if="attachment.external_link">
            <a
              :href="attachment.external_link"
              target="_blank"
            >
              <p class="text-primary font-medium text-sm line-clamp-1 my-2">
                Voir sur le navigateur
                <icon name="mdi-open-in-new" color="primary" />
              </p>
            </a>
            <iframe :src="attachment.external_link" class="w-[100%] h-[70vh]" />
          </div>
        </div>
      </div>
    </DialogScrollContent>
  </Dialog>
</template>

<style scoped>

</style>
